"use client";

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiUrl } from "@/shared/api/config";
import { subscribeToMatches } from "@/shared/lib/signalr/match-live-connection";
import { useAuthStore } from "@/shared/model/auth-store";

import { matchVetoApi } from "./client";
import type { MatchSideVetoPickRequest } from "../model/types";

const vetoQueryKey = (matchId: string) => ["match-veto", matchId] as const;

/**
 * KAN-983: бэкенд шлёт VetoStateUpdated в группу матча, но экран вето его не
 * слушал и опрашивал состояние каждые 2 секунды весь ход. Теперь ход соперника
 * приходит пушем сразу, а опрос с живым пушем — редкая страховка. Без пуша
 * (аноним: хаб под [Authorize], обрыв) остаются прежние 2 секунды: у хода
 * дедлайн, отставать на страховочный интервал нельзя.
 */
export const VETO_FAST_POLL_MS = 2000;
export const VETO_SAFETY_POLL_MS = 15_000;

/** Интервал опроса вето: вне хода — не опрашиваем; в ходе — по тому, жив ли пуш. */
export function vetoPollInterval(
  data: { status?: string | null; turnDeadlineUtc?: string | null } | null | undefined,
  pushLive: boolean,
): number | false {
  if (!(data?.status === "InProgress" && data?.turnDeadlineUtc)) return false;
  return pushLive ? VETO_SAFETY_POLL_MS : VETO_FAST_POLL_MS;
}

export function useMatchVetoState(matchId: string | undefined, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const enabled = (options?.enabled ?? true) && !!matchId;
  const [pushLive, setPushLive] = useState(false);

  useEffect(() => {
    if (!enabled || !isAuthenticated || !matchId) return;
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: vetoQueryKey(matchId) });
    };
    const unsubscribe = subscribeToMatches(
      apiUrl,
      [matchId],
      { VetoStateUpdated: refresh },
      { onReconnected: refresh, onLiveChange: setPushLive },
    );
    return () => {
      unsubscribe();
      setPushLive(false);
    };
  }, [enabled, isAuthenticated, matchId, queryClient]);

  const query = useQuery({
    queryKey: vetoQueryKey(matchId ?? ""),
    queryFn: () => matchVetoApi.getState(matchId!),
    enabled,
    refetchInterval: (query) => vetoPollInterval(query.state.data, pushLive),
  });
  return { ...query, vetoState: query.data ?? null };
}

export function useMatchVetoPick(matchId: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (request: MatchSideVetoPickRequest) => matchVetoApi.pickSide(matchId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vetoQueryKey(matchId) });
    },
  });
  return mutation;
}
